import type { Expense, ExpenseCategory } from '../types/expense';

const STORAGE_KEY = 'debtracker_expenses';

const generateId = (): string =>
  Math.random().toString(36).substring(2, 10) + Date.now().toString(36);

// ─── CRUD ─────────────────────────────────────────────────────────────────────
export const getAllExpenses = (): Expense[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Expense[]) : [];
  } catch {
    return [];
  }
};

const saveAll = (expenses: Expense[]) =>
  localStorage.setItem(STORAGE_KEY, JSON.stringify(expenses));

export const createExpense = (
  data: Omit<Expense, 'id' | 'createdAt' | 'updatedAt'>
): Expense => {
  const now = new Date().toISOString();
  const expense: Expense = { ...data, id: generateId(), createdAt: now, updatedAt: now };
  saveAll([...getAllExpenses(), expense]);
  return expense;
};

export const updateExpense = (
  id: string,
  updates: Partial<Omit<Expense, 'id' | 'createdAt'>>
): Expense | null => {
  const all = getAllExpenses();
  const idx = all.findIndex((e) => e.id === id);
  if (idx === -1) return null;
  all[idx] = { ...all[idx], ...updates, updatedAt: new Date().toISOString() };
  saveAll(all);
  return all[idx];
};

export const deleteExpense = (id: string): boolean => {
  const all = getAllExpenses();
  const filtered = all.filter((e) => e.id !== id);
  if (filtered.length === all.length) return false;
  saveAll(filtered);
  return true;
};

// ─── Grouping helpers ─────────────────────────────────────────────────────────
export const groupByDay = (expenses: Expense[]): Map<string, Expense[]> => {
  const map = new Map<string, Expense[]>();
  const sorted = [...expenses].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );
  for (const e of sorted) {
    const existing = map.get(e.date) ?? [];
    map.set(e.date, [...existing, e]);
  }
  return map;
};

export const groupByMonth = (expenses: Expense[]): Map<string, Expense[]> => {
  const map = new Map<string, Expense[]>();
  for (const e of expenses) {
    const key = e.date.substring(0, 7); // YYYY-MM
    const existing = map.get(key) ?? [];
    map.set(key, [...existing, e]);
  }
  return map;
};

// ─── Stats ─────────────────────────────────────────────────────────────────────
export interface SpendingStats {
  today: number;
  thisWeek: number;
  thisMonth: number;
  lastMonth: number;
  categoryBreakdown: Record<ExpenseCategory, number>; // this month
  dailyAvgThisMonth: number;
}

export const getSpendingStats = (): SpendingStats => {
  const all = getAllExpenses();
  const now = new Date();
  const todayStr = now.toISOString().split('T')[0];
  const thisMonthKey = todayStr.substring(0, 7);
  const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastMonthKey = lastMonthDate.toISOString().substring(0, 7);

  // Start of this week (Monday)
  const dayOfWeek = now.getDay(); // 0=Sun
  const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() - mondayOffset);
  const weekStartStr = weekStart.toISOString().split('T')[0];

  let today = 0;
  let thisWeek = 0;
  let thisMonth = 0;
  let lastMonth = 0;
  const categoryBreakdown: Partial<Record<ExpenseCategory, number>> = {};

  for (const e of all) {
    if (e.date === todayStr) today += e.amount;
    if (e.date >= weekStartStr && e.date <= todayStr) thisWeek += e.amount;
    if (e.date.startsWith(thisMonthKey)) {
      thisMonth += e.amount;
      categoryBreakdown[e.category] = (categoryBreakdown[e.category] ?? 0) + e.amount;
    }
    if (e.date.startsWith(lastMonthKey)) lastMonth += e.amount;
  }

  const daysElapsedInMonth = now.getDate();
  const dailyAvgThisMonth = daysElapsedInMonth > 0 ? thisMonth / daysElapsedInMonth : 0;

  return {
    today,
    thisWeek,
    thisMonth,
    lastMonth,
    categoryBreakdown: categoryBreakdown as Record<ExpenseCategory, number>,
    dailyAvgThisMonth,
  };
};
